import { useState } from "react";
import { ReceiptScan } from "@/components/ReceiptScan";
import { ReceiptConfidenceNotice } from "@/components/ReceiptConfidenceNotice";
import { isPlausibleReceiptDate } from "@/lib/receiptDate";
import { ApiError, demoApi } from "@/api/client";
import type { Account, ReceiptAnalysis, ReceiptConfidence, Vendor } from "@/api/types";
import "./ReceiptCapture.css";

type SubmitState =
  | { kind: "idle" }
  | { kind: "submitting" }
  /**
   * `remembered` is BOOTH BEAT 2 — see handleAnalyzed. The saved line says something
   * different when the category came from a mapping the operator taught earlier than
   * when it came from the model's own guess.
   */
  | { kind: "saved"; vendorName: string; remembered: boolean; categoryName: string | null }
  | { kind: "needs-review"; missing: string[] }
  | { kind: "not-ready" }
  | { kind: "error"; message: string };

interface ReceiptCaptureProps {
  /** The business the entry belongs to. `null` while reference data is loading —
   *  the scan is refused rather than filed against a guessed entity. */
  entityId: string | null;
  /** For naming the vendor in the success line. The caller already holds this map;
   *  refetching it here would be a second copy of the reference data. */
  vendorById: Map<string, Vendor>;
  /** For naming the CATEGORY on a remembered scan (booth beat 2). Dashboard already
   *  builds this map for its feed rows; this is the same object, not a second fetch. */
  accountById?: Map<string, Account>;
  /** Where an unreadable scan sends the operator to type it by hand. */
  onNavigate?: (path: string) => void;
}

/**
 * Scan a receipt and file it — the operator's half of the receipt demo.
 *
 * WAS ITS OWN SCREEN, and should not have been. `/capture` rendered this scan box, a
 * page header, and a "Today" feed that was empty until someone used it — three quarters
 * of a screen's whitespace around one control, reached by a Dashboard button labelled
 * "Quick entry" that took you somewhere titled "Capture expense". It also cost a fifth
 * nav tab, and "Quick entry" was already the name of `/entry`, the visitor's phone flow,
 * so one label pointed at two destinations.
 *
 * <p>
 * Extracting the behaviour into a component rather than moving the JSX into Dashboard.tsx
 * keeps that file from growing a second responsibility, and keeps this testable on its
 * own terms. The logic is unchanged from the screen it replaces — the honesty rules below
 * were learned the hard way and are carried over verbatim.
 * </p>
 *
 * <p>
 * THE FEED IS THE CALLER'S. This renders no activity list: on the Dashboard the scanned
 * entry lands in "Recent activity", which is the same live `useEntries` stream the old
 * screen's feed read. One feed, beside the scan box, is the actual booth motion — scan a
 * receipt and watch it appear — which the old two-screen split broke in half.
 * </p>
 */
export function ReceiptCapture({ entityId, vendorById, accountById, onNavigate }: ReceiptCaptureProps) {
  const [submit, setSubmit] = useState<SubmitState>({ kind: "idle" });
  const [scanConfidence, setScanConfidence] = useState<ReceiptConfidence | null>(null);
  const [scanDate, setScanDate] = useState<string | null>(null);
  // ef-011: set when the file was stored but deliberately not read (a PDF). Distinct from
  // a low-confidence read, and the two must never be shown together.
  const [notAnalyzed, setNotAnalyzed] = useState<string | null>(null);

  async function handleAnalyzed(analysis: ReceiptAnalysis): Promise<void> {
    const { vendorId, accountId, amount } = analysis;

    // Held for the notice below. This files a complete scan IMMEDIATELY, which makes a
    // poor read MORE worth surfacing than on /entry, not less: there the visitor is
    // looking straight at the fields and can correct them, whereas here the entry is
    // already in the ledger by the time anyone reads it.
    setScanConfidence(analysis.confidence);
    setScanDate(isPlausibleReceiptDate(analysis.date) ? analysis.date : null);
    setNotAnalyzed(analysis.notAnalyzedReason ?? null);

    /*
      A MISSING ENTITY IS NOT AN UNREADABLE RECEIPT, and it used to be reported as one.

      `entityId` is the app's own state — it arrives with reference data, over the network —
      while vendor, category and amount are what the model got off the photo. Listing all
      four as things that could not be "read" produced, verbatim, **"Couldn't fully read that
      receipt — missing business entity. Nothing was saved."** about a scan the model read
      perfectly. The visitor is shown a photograph of their receipt and told it was too poor
      to use, because the app had not finished loading.

      `ReceiptScan` now disables the control until the entity is known, so this should be
      unreachable from the button. It is kept, and told apart, because "should be
      unreachable" is not a thing to write copy against — a queued scan draining, or an
      entity that resolves and is lost, still arrives here.
    */
    if (!entityId) {
      setSubmit({ kind: "not-ready" });
      return;
    }

    // Never guess a vendor, category or amount to force a save — an ambiguous scan is
    // reported as exactly that, honestly, rather than silently filed under a wrong or
    // invented category.
    if (!vendorId || !accountId || amount == null || amount <= 0) {
      const missing: string[] = [];
      if (!vendorId) missing.push("vendor");
      if (!accountId) missing.push("category");
      if (amount == null || amount <= 0) missing.push("amount");
      setSubmit({ kind: "needs-review", missing });
      return;
    }

    setSubmit({ kind: "submitting" });
    try {
      // source: "Receipt" is REQUIRED, not decoration — the caller's feed and the
      // uncategorized-receipt count both key on it, so an entry saved without it is
      // invisible while the status line below says it landed. It did not.
      // The receipt's own date, when it is believable: this files the entry immediately,
      // so this is the only chance to book it on the day it actually happened.
      const occurredOn = isPlausibleReceiptDate(analysis.date) ? analysis.date! : undefined;
      await demoApi.createEntry({
        entityId,
        vendorId,
        accountId,
        amount,
        source: "Receipt",
        ...(occurredOn ? { occurredOn } : {}),
        // ef-006: attach the photograph the API kept. This is the half of the receipt
        // scan that needs no network — import and document now, categorise later.
        ...(analysis.receiptImageId ? { receiptImageId: analysis.receiptImageId } : {}),
      });
      const vendorName = vendorById.get(vendorId)?.name ?? "that vendor";

      // BOOTH BEAT 2 — "auto-maps instantly, zero touch. It learned."
      //
      // The API has returned accountIsConfirmed since the mapping feature shipped and
      // NOTHING read it: grep found one hit, the type declaration. So the second scan of a
      // known vendor filed correctly and looked EXACTLY like the first, and the beat the
      // trade-show spec calls the deterministic half — "cannot be wrong ... a vendor→account
      // lookup, not inference" — was invisible to the person it is performed for.
      //
      // This is the whole difference between the two beats. Beat 1 is a guess the operator
      // may correct; beat 2 is the system repeating a decision it was taught, and saying so
      // is the point. Christy's line over it is "it learned".
      setSubmit({
        kind: "saved",
        vendorName,
        remembered: analysis.accountIsConfirmed,
        categoryName: accountById?.get(accountId)?.name ?? null,
      });

      // Same "the correction teaches the system" write QuickEntry makes — deliberately
      // not awaited into the success path (see QuickEntry.tsx).
      void demoApi
        .confirmMapping({ vendorId, accountId })
        .catch((err: unknown) =>
          console.warn("Entry saved, but the vendor mapping was not recorded.", err),
        );
    } catch (err) {
      setSubmit({
        kind: "error",
        message: err instanceof ApiError ? err.message : "Could not save that entry — try again.",
      });
    }
  }

  return (
    <section className="receipt-capture" aria-labelledby="receipt-capture-heading">
      <h2 id="receipt-capture-heading" className="receipt-capture__title">
        Capture expense
      </h2>
      <p className="receipt-capture__hint">Snap a receipt — we categorize it for you</p>

      <ReceiptScan onAnalyzed={(a) => void handleAnalyzed(a)} entityId={entityId} />

      {scanConfidence && <ReceiptConfidenceNotice confidence={scanConfidence} date={scanDate} />}

      {/* ef-011 — a document was kept but deliberately not read. Never rendered alongside
          the confidence notice: the API returns a null confidence in exactly this case, so
          the two are mutually exclusive by construction rather than by this markup. */}
      {notAnalyzed && (
        <p className="receipt-capture__status" role="status" data-state="not-analyzed">
          {notAnalyzed}
        </p>
      )}

      {submit.kind === "submitting" && (
        <p className="receipt-capture__status" role="status">
          Saving the categorized entry…
        </p>
      )}
      {submit.kind === "saved" && (
        <p className="receipt-capture__status receipt-capture__status--ok" role="status">
          {submit.remembered ? (
            <>
              {/* Beat 2. Names the vendor AND the category so a prospect sees a specific
                  decision being recalled, not a generic reassurance. "Remembered" is the
                  word doing the work — it claims memory, not cleverness, which is the
                  claim the product can actually keep. */}
              Remembered {submit.vendorName}
              {submit.categoryName ? <> — filed under {submit.categoryName}, the way you mapped it before</> : <> — filed the way you mapped it before</>}.
            </>
          ) : (
            <>Categorized automatically — {submit.vendorName} just landed in Recent activity.</>
          )}
        </p>
      )}
      {submit.kind === "not-ready" && (
        <p className="receipt-capture__status receipt-capture__status--warn" role="status" data-state="not-ready">
          Still getting your farm ready — take that photo again in a moment. Nothing was
          saved, and there is nothing wrong with the receipt.
        </p>
      )}

      {submit.kind === "needs-review" && (
        <p className="receipt-capture__status receipt-capture__status--warn" role="status">
          Couldn&apos;t fully read that receipt — missing {submit.missing.join(", ")}. Nothing was
          saved;{" "}
          {onNavigate ? (
            <button
              type="button"
              className="receipt-capture__inline-link"
              onClick={() => onNavigate("/entry")}
            >
              enter it by hand instead
            </button>
          ) : (
            "enter it by hand instead"
          )}
          .
        </p>
      )}
      {submit.kind === "error" && (
        <p className="receipt-capture__status receipt-capture__status--error" role="alert">
          {submit.message}
        </p>
      )}
    </section>
  );
}
